import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState<"loading" | "success" | "failed">("loading");
  const [paymentDetails, setPaymentDetails] = useState<any>(null);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    const verifyPayment = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("check-order-status", {
          body: { sessionId }
        });

        if (error) {
          throw error;
        }

        if (data.paymentStatus === "paid") {
          setStatus("success");
          toast({
            title: "Payment Successful!",
            description: "Your beat license has been purchased successfully.",
          });
        } else {
          setStatus("failed");
        }

        setPaymentDetails(data);
      } catch (error) {
        if (import.meta.env.DEV) console.error("Payment verification failed:", error);
        setStatus("failed");
        toast({
          title: "Verification Error",
          description: "Failed to verify payment status.",
          variant: "destructive",
        });
      }
    };

    verifyPayment();
  }, [searchParams, toast]);

  return (
    <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            {status === "loading" && (
              <Loader2 className="h-16 w-16 text-primary animate-spin" />
            )}
            {status === "success" && (
              <CheckCircle className="h-16 w-16 text-green-500" />
            )}
            {status === "failed" && (
              <XCircle className="h-16 w-16 text-red-500" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {status === "loading" && "Verifying Payment..."}
            {status === "success" && "Payment Successful!"}
            {status === "failed" && "Payment Failed"}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">
            {status === "loading" && "Please wait while we verify your payment..."}
            {status === "success" && "Your beat license has been purchased successfully. You can now download your files."}
            {status === "failed" && "There was an issue with your payment. Please try again or contact support."}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button onClick={() => navigate("/beats")} variant="outline">
              Back to Beats
            </Button>
            {status === "success" && (
              <Button onClick={() => navigate("/", { state: { openPurchaseHistory: true } })}>
                View Purchases
              </Button>
            )}
            {status === "failed" && (
              <Button onClick={() => navigate("/beats")}>
                Try Again
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
